import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Package, Plus, AlertTriangle, Truck, Clock, PackageCheck, Send } from "lucide-react";
import { PoStatusBadge, money, usePoActions, PO_STATUS } from "@/components/procurement/poUtils";
import { ProcurementListDialog } from "@/components/procurement/ProcurementListDialog";
import { PoDetailDialog } from "@/components/procurement/PoDetailDialog";
import { ReceiveDialog } from "@/components/procurement/ReceiveDialog";

const Tile = ({ icon: Icon, label, value, tone = "slate", onClick, active, testId }) => (
  <button onClick={onClick} data-testid={testId} className={`text-left p-4 rounded-xl border transition-colors ${active ? "border-indigo-400 bg-indigo-50" : "bg-white hover:bg-slate-50"}`}>
    <div className="flex items-center gap-2 text-xs text-slate-500"><Icon className={`w-4 h-4 text-${tone}-600`} /> {label}</div>
    <p className="text-2xl font-bold mt-1" style={{ fontFamily: "Manrope, sans-serif" }}>{value}</p>
  </button>
);

export default function ProcurementPage() {
  const [lists, setLists] = useState([]);
  const [summary, setSummary] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [filters, setFilters] = useState({ status: "open", supplier_id: "", overdue: false, q: "" });
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [receiving, setReceiving] = useState(null);

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (filters.status) p.set("status", filters.status);
    if (filters.supplier_id) p.set("supplier_id", filters.supplier_id);
    if (filters.overdue) p.set("overdue", "true");
    axios.get(`${API}/procurement/lists?${p}`).then(r => setLists(r.data || [])).catch(() => setLists([]));
    axios.get(`${API}/procurement/summary`).then(r => setSummary(r.data)).catch(() => {});
  }, [filters.status, filters.supplier_id, filters.overdue]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { axios.get(`${API}/suppliers`).then(r => setSuppliers(r.data || [])).catch(() => {}); }, []);
  const actions = usePoActions(load);

  const q = filters.q.toLowerCase();
  const visible = lists.filter(l => !q || [l.po_number, l.supplier_name, l.project_name, l.customer_name, l.title, ...(l.items || []).map(i => i.name)].filter(Boolean).some(s => s.toLowerCase().includes(q)));

  return (
    <div className="space-y-6" data-testid="procurement-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>Material Procurement</h1>
          <p className="text-sm text-slate-500">Purchase orders to suppliers — what's on order, for which job, and when it lands.</p>
        </div>
        <Button onClick={() => setShowNew(true)} data-testid="new-procurement-list-btn"><Plus className="w-4 h-4 mr-2" /> New List</Button>
      </div>

      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Tile icon={Package} label="Open lists" value={summary.open_count} tone="indigo" active={filters.status === "open" && !filters.overdue} onClick={() => setFilters({ ...filters, status: "open", overdue: false })} testId="tile-open" />
          <Tile icon={Truck} label="On order value" value={money(summary.open_total)} tone="blue" active={filters.status === "sent"} onClick={() => setFilters({ ...filters, status: "sent", overdue: false })} testId="tile-on-order" />
          <Tile icon={Clock} label="Awaiting approval" value={summary.by_status?.pending_approval || 0} tone="amber" active={filters.status === "pending_approval"} onClick={() => setFilters({ ...filters, status: "pending_approval", overdue: false })} testId="tile-pending" />
          <Tile icon={AlertTriangle} label="ETA overdue" value={summary.overdue_count} tone="red" active={filters.overdue} onClick={() => setFilters({ ...filters, status: "open", overdue: !filters.overdue })} testId="tile-overdue" />
        </div>
      )}

      <div className="flex flex-wrap gap-2 items-center">
        <select className="border rounded-md px-3 py-2 text-sm bg-white" value={filters.status} onChange={e => setFilters({ ...filters, status: e.target.value, overdue: false })} data-testid="filter-status">
          <option value="open">All open</option>
          {Object.entries(PO_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          <option value="">Everything</option>
        </select>
        <select className="border rounded-md px-3 py-2 text-sm bg-white" value={filters.supplier_id} onChange={e => setFilters({ ...filters, supplier_id: e.target.value })} data-testid="filter-supplier">
          <option value="">All suppliers</option>
          {suppliers.map(s => <option key={s.supplier_id} value={s.supplier_id}>{s.company_name}</option>)}
        </select>
        <Input className="w-56" placeholder="Search PO, project, item…" value={filters.q} onChange={e => setFilters({ ...filters, q: e.target.value })} data-testid="filter-search" />
      </div>

      <Card>
        <CardContent className="p-0">
          {visible.length === 0 ? (
            <div className="text-center py-12"><Package className="w-12 h-12 mx-auto mb-3 text-slate-300" /><p className="text-slate-500">No lists match</p></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs text-slate-500 text-left"><tr><th className="p-3">PO</th><th className="p-3">Supplier</th><th className="p-3">Project</th><th className="p-3 text-right">Items</th><th className="p-3 text-right">Total</th><th className="p-3">ETA</th><th className="p-3">Status</th><th className="p-3"></th></tr></thead>
                <tbody>
                  {visible.map(po => {
                    const recv = po.items.reduce((s, i) => s + (Number(i.quantity_received) || 0), 0);
                    const ordered = po.items.reduce((s, i) => s + (Number(i.quantity) || 0), 0);
                    return (
                      <tr key={po.po_id} className="border-t hover:bg-slate-50 cursor-pointer" onClick={() => setViewing(po)} data-testid={`po-table-row-${po.po_id}`}>
                        <td className="p-3"><p className="font-semibold">{po.po_number}</p>{po.title && <p className="text-xs text-slate-500 truncate max-w-[180px]">{po.title}</p>}</td>
                        <td className="p-3">{po.supplier_name}</td>
                        <td className="p-3">{po.project_name ? <><p>{po.project_name}</p><p className="text-xs text-slate-500">{po.customer_name}</p></> : <span className="text-slate-400">Stock</span>}</td>
                        <td className="p-3 text-right">{po.items.length}<span className="text-xs text-slate-400"> · {recv}/{ordered} recv'd</span></td>
                        <td className="p-3 text-right font-medium">{money(po.total)}</td>
                        <td className={`p-3 whitespace-nowrap ${po.is_overdue ? "text-red-600 font-medium" : ""}`}>{po.expected_date || "—"}</td>
                        <td className="p-3"><PoStatusBadge status={po.status} overdue={po.is_overdue} /></td>
                        <td className="p-3 text-right whitespace-nowrap" onClick={e => e.stopPropagation()}>
                          {po.status === "draft" && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => actions.send(po)} data-testid={`row-send-${po.po_id}`}><Send className="w-3 h-3 mr-1" /> Send</Button>}
                          {["sent", "partially_received"].includes(po.status) && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setReceiving(po)} data-testid={`row-receive-${po.po_id}`}><PackageCheck className="w-3 h-3 mr-1" /> Receive</Button>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <ProcurementListDialog open={showNew || !!editing} onOpenChange={(v) => { if (!v) { setShowNew(false); setEditing(null); } }} po={editing} onSaved={load} />
      <PoDetailDialog po={viewing} open={!!viewing} onOpenChange={(v) => { if (!v) setViewing(null); }} onChanged={load} onEdit={setEditing} onReceive={setReceiving} />
      <ReceiveDialog po={receiving} open={!!receiving} onOpenChange={(v) => { if (!v) setReceiving(null); }} onDone={load} />
    </div>
  );
}
